import {
	addUnit,
	clone,
	createDuration,
	dateToUTCString,
	endOf,
	formatDate,
	getDiff,
	humanizeDuration,
	isSame,
	localOrUtcTimestampToLocalDate,
	parseDate,
	setTimeFrom,
	startOf,
} from "@sembark-travel/datetime-utils"
import {
	Alert,
	Box,
	Button,
	Card,
	CardBody,
	Col,
	ColOwnProps,
	Divider,
	Dropdown,
	Grid,
	GridOwnProps,
	Heading,
	Icons,
	Inline,
	Money,
	Select,
	Spinner,
	Stack,
	Text,
	Time,
} from "@sembark-travel/ui-base"
import {
	arrayMutators,
	CheckboxInputField,
	EmptyNumberValidator,
	FieldArray,
	FieldGroup,
	Form,
	FormApi,
	GetFieldValue,
	MarkdownInputField,
	MoneyInputField,
	SelectField,
	SubmissionError,
	SwitchInputField,
	TextAreaInputField,
	useFieldValue,
	useForm,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR, XHRInstance } from "@sembark-travel/xhr"
import { useEffect, useMemo, useRef, useState } from "react"
import { $PropertyType, Optional, Required } from "utility-types"
import * as Validator from "yup"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { ICab, SelectCabs } from "../Cabs"
import {
	CabCapacityWarningMessage,
	ICabType,
	SelectCabTypes,
	SelectFromCabTypeList,
} from "../CabTypes"
import config from "../config"
import {
	useCurrenciesCodesOfTenant,
	useFunctionalCurrencyOfTenant,
} from "../Currencies"
import { TDriver, SelectDriver } from "../Drivers"
import { TTourist, TTouristGroup, TTouristMember } from "../Tourists"
import {
	TBookingTouristUpdate,
	TouristAssignmentInputField,
} from "../Tourists/TouristAssignmentInputField"
import { createBookingDateSelectionOptionsFromStartAndEndDates } from "../TransportServicePrices/utils"
import {
	ISupplier,
	SelectTransporServiceProviders,
	TransportPaymentPreferencesForm,
} from "../Supplier"
import {
	ITransportService,
	SelectTransportServiceLocations,
	TTransportServiceLocation,
	TTransportServiceLocationPoint,
	HotelAsPickupDropToggleField,
} from "../TransportServices"
import { extractLocationString } from "../TransportServices/utils"
import {
	getClosedDates,
	showClosedServicesAlert,
} from "../TravelActivities/store"
import { isTicketTypeClosedOnDate } from "../TravelActivities/utils"
import { ITrip } from "../Trips"
import { SelectTrips } from "../Trips/List"
import { WarnForExistingScheduledCabsForSupplier } from "./AssignServiceProviders"
import { ICabSchedule, TScheduledCabMember } from "./store"

function XHR(xhr: XHRInstance) {
	return {
		async createSchedule(data: unknown): Promise<Array<ICabSchedule>> {
			return xhr.post("/cab-schedules", data).then((resp) => resp.data.data)
		},
	}
}

export function CreateSchedule({
	date,
	onCancel,
	onSuccess,
	trip,
}: {
	date?: Date
	trip?: ScheduleFormProps["trip"]
	onCancel?: () => void
	onSuccess?: () => void
}) {
	const xhr = useXHR()
	return (
		<ScheduleForm
			trip={trip}
			pickupTime={date ? startOf(date, "day") : undefined}
			dropTime={date ? endOf(date, "day") : undefined}
			onSubmit={XHR(xhr).createSchedule}
			onSuccess={onSuccess}
			onCancel={onCancel}
			readOnlyTrip={Boolean(trip)}
		/>
	)
}

interface NewScheduleRequestData {
	pickupTime?: string | Date
	dropTime?: string | Date
	pickup_time?: string
	pick_up_location?: string | TTransportServiceLocationPoint
	drop_location?: string | TTransportServiceLocationPoint
	use_hotel_as_pickup?: string | null
	use_hotel_as_drop?: string | null
	durationIso?: string
	transportService?: $PropertyType<ICabSchedule, "transport_service">
	transport_service_location?: TTransportServiceLocation
	remarks?: string
	customerRemarks?: string
	supplierRemarks?: string
	hasSameCustomerAndSupplierRemarks?: boolean
	comments?: string
	hasCabTimeVariation?: boolean
	trip?: Pick<
		ITrip,
		| "id"
		| "destinations"
		| "start_date"
		| "start_date_local"
		| "end_date"
		| "end_date_local"
		| "pax_string"
		| "children"
		| "no_of_adults"
	>
	cabs?: Array<
		Optional<
			Omit<$PropertyType<ICabSchedule, "cabs">[number], "trip" | "cab_schedule">
		> & {
			fetching_price?: boolean
			remarks?: string
			supplier_remarks?: string
			customer_remarks?: string
			has_same_customer_and_supplier_remarks: boolean
			comments?: string
			pickup_time?: string
			pick_up_location?: string | TTransportServiceLocationPoint
			drop_location?: string | TTransportServiceLocationPoint
			use_hotel_as_pickup?: string | null
			use_hotel_as_drop?: string | null
			dropoff_time?: string
			duration?: {
				iso: string
				name: string
			}
			duration_iso?: string | null
		}
	>
	readOnlyTrip?: boolean
}

const validationSchema = validateFormValues(
	Validator.object().shape({
		pickup: Validator.date().required("Pickup date/time is required"),
		// drop: Validator.date()
		//   .required("Pickup date/time is required")
		//   .when(
		//     "pickup",
		//     ([value], schema) =>
		//       !value ? schema : schema.min(value, "Drop should be after pickup")
		//   ),
		no_of_cabs: EmptyNumberValidator()
			.positive("Number of cabs should be positive integer")
			.typeError("Number of cab should be integer"),
		transport_service_location: Validator.object().required(
			"Transport service location is required"
		),
		transport_service: Validator.object().required(
			"Transport service is required"
		),
		trip: Validator.object()
			.typeError("Trip is required.")
			.required("Trip is required."),
		cabs: Validator.array()
			.of(
				Validator.object().shape({
					cab_type: Validator.object()
						.typeError("Cab Type is required")
						.required("Cab Type is required."),
					transport_service_provider: Validator.mixed().nullable(),
					driver: Validator.mixed().nullable(),
					cab: Validator.mixed().nullable(),
					booked_price: EmptyNumberValidator()
						.typeError("Booked price should be a positive number")
						.required("Please enter at least 0"),
					confirmation_details: Validator.string()
						.nullable()
						.max(1000, "Maximum 1000 characters allowed"),
				})
			)
			.min(1, "Please provide atleast one cab's details"),
	})
)

interface ScheduleFormProps extends Omit<
	NewScheduleRequestData,
	| "pick_up_location"
	| "drop_location"
	| "use_hotel_as_pickup"
	| "use_hotel_as_drop"
> {
	onSubmit: (data: unknown) => Promise<ICabSchedule[]>
	onCancel?: () => void
	onSuccess?: () => void
	trip?: ITrip
	tourists?: ICabSchedule["tourists"]
	members?: ICabSchedule["members"]
	tourist?: ICabSchedule["tourist"]
	pickupLocation?: string | TTransportServiceLocationPoint
	dropLocation?: string | TTransportServiceLocationPoint
	useHotelAsPickup?: string | null
	useHotelAsDrop?: string | null
}

interface IScheduleFormCab extends Omit<
	NonNullable<Required<NewScheduleRequestData>["cabs"]>[number],
	"members"
> {
	members?: Array<Omit<TScheduledCabMember, "id">>
	primary_contact?: number | null
	are_remarks_separated: boolean
}

function CabBookedPriceField({
	name,
	allCurrencies,
	hasPermission,
}: {
	name: string
	allCurrencies: string[]
	hasPermission: (permission: string) => boolean
}) {
	const { value: givenPrice } = useFieldValue<number | undefined>(
		`${name}.given_price`
	)
	const { value: currency } = useFieldValue<string | undefined>(
		`${name}.currency`
	)
	const { value: isFetching } = useFieldValue<boolean>(`${name}.fetching_price`)
	const { value: calculatedPrice } = useFieldValue<number | undefined>(
		`${name}.calculated_price`
	)

	return (
		<Box position="relative" cursor={isFetching ? "wait" : undefined}>
			<MoneyInputField
				currencyFieldName={`${name}.currency`}
				name={`${name}.booked_price`}
				label="Booked Price"
				currencies={calculatedPrice && currency ? [currency] : allCurrencies}
				help={
					<>
						{hasPermission(PERMISSIONS.MANAGE_TRIP_OWNERS) &&
						givenPrice &&
						currency ? (
							<Text fontSize="sm" color="muted">
								Selling price:{" "}
								<Money amount={givenPrice} currency={currency} showCurrency />
							</Text>
						) : null}
						{calculatedPrice && currency ? (
							<Text fontSize="sm" color="muted">
								Current Booking Rate:{" "}
								<Money
									amount={calculatedPrice}
									currency={currency}
									showCurrency
								/>
							</Text>
						) : null}
					</>
				}
			/>
			{isFetching && (
				<Box
					position="absolute"
					top="0"
					right="0"
					bottom="0"
					left="0"
					bgColor="default"
					opacity="70"
					display="flex"
					alignItems="center"
					justifyContent="center"
					rounded="md"
					zIndex="10"
				>
					<Spinner padding="2" />
				</Box>
			)}
		</Box>
	)
}

type ScheduleFormData = {
	pickup: Date
	pickup_time: string | undefined
	pick_up_location: string | TTransportServiceLocationPoint | null
	drop_location: string | TTransportServiceLocationPoint | null
	use_hotel_as_pickup: string | null
	use_hotel_as_drop: string | null
	duration:
		| {
				iso: string
				name: string
		  }
		| undefined
	remarks: string
	supplier_remarks?: string
	customer_remarks?: string
	are_remarks_separated: boolean
	comments: string
	has_cab_time_variation: number
	cabs: IScheduleFormCab[] | undefined
	transport_service_location: TTransportServiceLocation | null
	transport_service:
		| (ITransportService & {
				label: string
		  })
		| undefined
	trip: ITrip | undefined
	members: (TTouristMember & { id: string })[] | undefined
	tourist: TTourist | undefined
	tourists:
		| {
				id: number
				remarks: string | null
		  }[]
		| undefined
	all_tourists: TTourist[] | undefined
	primary_contact: number | null
}

// Kept this component here, as it support for the single location selection with hotelaspickupdroptoggle
function LocationWithHotelAsToggleField({
	type,
	fieldName,
	useHotelAsFieldName,
	options,
}: {
	type: "pickup" | "drop"
	fieldName: string
	useHotelAsFieldName: string
	options: Array<{ name: string; id: string | number }>
}) {
	return (
		<GetFieldValue<string | null> name={useHotelAsFieldName}>
			{({ value: useHotelAs }) => (
				<Stack gap="3" flex="1">
					<Box
						opacity={useHotelAs ? "50" : "100"}
						cursor={useHotelAs ? "disabled" : undefined}
						pointerEvents={useHotelAs ? "none" : undefined}
					>
						<SelectField
							label={type === "pickup" ? "Pickup Location" : "Drop Location"}
							name={fieldName}
							select={Select}
							clearable={true}
							creatable={true}
							options={options}
						/>
					</Box>
					<HotelAsPickupDropToggleField
						name={useHotelAsFieldName}
						for={type}
						clearFieldName={fieldName}
					/>
				</Stack>
			)}
		</GetFieldValue>
	)
}

export function PickupDropLocationFields({
	namePrefix = "",
	pickupLocations = [],
	dropLocations = [],
	// added colProps and GridProps for different layouts compatibility
	colProps = { xs: 12, sm: 6, lg: 4 },
	gridProps = {},
}: {
	namePrefix?: string
	pickupLocations?: ReadonlyArray<string | TTransportServiceLocationPoint>
	dropLocations?: ReadonlyArray<string | TTransportServiceLocationPoint>
	colProps?: ColOwnProps
	gridProps?: GridOwnProps
}) {
	const pickupFieldName = namePrefix
		? `${namePrefix}.pick_up_location`
		: "pick_up_location"
	const dropFieldName = namePrefix
		? `${namePrefix}.drop_location`
		: "drop_location"
	const useHotelAsPickupFieldName = namePrefix
		? `${namePrefix}.use_hotel_as_pickup`
		: "use_hotel_as_pickup"
	const useHotelAsDropFieldName = namePrefix
		? `${namePrefix}.use_hotel_as_drop`
		: "use_hotel_as_drop"

	const pickupOptions = pickupLocations.map((l) => ({
		name: typeof l === "string" ? l : l.name,
		id: typeof l === "string" ? l : l.id,
	}))

	const dropOptions = dropLocations.map((l) => ({
		name: typeof l === "string" ? l : l.name,
		id: typeof l === "string" ? l : l.id,
	}))

	return (
		<Grid {...gridProps}>
			<Col {...colProps}>
				<LocationWithHotelAsToggleField
					type="pickup"
					fieldName={pickupFieldName}
					useHotelAsFieldName={useHotelAsPickupFieldName}
					options={pickupOptions}
				/>
			</Col>
			<Col {...colProps}>
				<LocationWithHotelAsToggleField
					type="drop"
					fieldName={dropFieldName}
					useHotelAsFieldName={useHotelAsDropFieldName}
					options={dropOptions}
				/>
			</Col>
		</Grid>
	)
}

export function ScheduleForm({
	pickupTime,
	transportService,
	remarks,
	comments,
	hasCabTimeVariation,
	trip,
	cabs,
	onSubmit,
	onCancel,
	onSuccess,
	readOnlyTrip,
	durationIso,
	tourists: cabScheduleTourists,
	members: cabScheduleMembers,
	tourist: cabScheduleTourist,
	pickupLocation,
	dropLocation,
	useHotelAsPickup,
	useHotelAsDrop,
	customerRemarks,
	supplierRemarks,
	hasSameCustomerAndSupplierRemarks,
}: ScheduleFormProps) {
	const functionalCurrency = useFunctionalCurrencyOfTenant()
	const xhr = useXHR()
	const [serviceOptions, setServiceOptions] = useState<
		Array<ITransportService & { label: string }>
	>(() => {
		// Initialize service options with ALL available services from the selected location
		if (transportService && transportService.locations?.services?.length) {
			return transportService.locations.services.map(
				(service: ITransportService) => ({
					...service,
					label: service.service,
				})
			)
		}
		return []
	})

	// Fetch all services from the selected location when component mounts
	useEffect(() => {
		if (transportService && transportService.locations) {
			// If we already have services, use them directly
			if (transportService.locations.services?.length) {
				const existingServices = transportService.locations.services.map(
					(service: ITransportService) => ({
						...service,
						label: service.service,
					})
				)
				setServiceOptions(existingServices)
			} else {
				// Only fetch if we don't have services available
				xhr
					.get(`/transport-service-locations/${transportService.locations.id}`)
					.then(
						(resp: { data: { data: TTransportServiceLocation } }) =>
							resp.data.data
					)
					.then((location: TTransportServiceLocation) => {
						// Set the options with all available services
						if (location.services?.length) {
							const allServices = location.services.map(
								(service: ITransportService) => ({
									...service,
									label: service.service,
								})
							)
							setServiceOptions(allServices)
						}
					})
					.catch((error: Error) => {
						showSnackbar(
							"Failed to fetch location services. Please try again.",
							{
								icon: <Icons.AttentionSolid color="danger" />,
								duration: 3000,
							}
						)
					})
			}
		}
	}, [transportService, xhr])

	// Function to handle service location changes
	const handleServiceLocationChange = (
		value: TTransportServiceLocation | null,
		form: FormApi<ScheduleFormData>
	) => {
		form.batch(() => {
			form.change("transport_service_location" as never, value as never)
			if (value?.services?.length) {
				const newServiceOptions = value.services.map(
					(service: ITransportService) => ({
						...service,
						label: service.service,
					})
				)
				setServiceOptions(newServiceOptions)
				form.change("transport_service" as never, newServiceOptions[0] as never)
			} else {
				setServiceOptions([])
				form.change("transport_service" as never, undefined as never)
			}
		})
	}

	const [initialValues] = useState<ScheduleFormData>(() => {
		const currency = trip
			? trip.destinations.map((d) => d.currency).at(0)
			: functionalCurrency.currency
		const initialCabs = [
			{
				id: null,
				cab_type: null,
				transport_service_provider: null,
				driver: null,
				cab: null,
				booked: false,
				booked_price: 0,
				calculated_price: 0,
				given_price: 0,
				already_booked: false,
				currency,
				confirmation_details: "",
				members: [],
				primary_contact: null,
				all_tourists: [],
				tourists: [],
				fetching_price: false,
				remarks: "",
				supplier_remarks: "",
				customer_remarks: "",
				has_same_customer_and_supplier_remarks: true,
				comments: "",
				pickup_time: null,
				pick_up_location: null,
				drop_location: null,
				use_hotel_as_pickup: null,
				use_hotel_as_drop: null,
				duration_iso: null,
				dropoff_time: null,
			},
		] as unknown as Array<IScheduleFormCab>
		const pickup_date = parseDate(
			pickupTime
				? pickupTime
				: startOf(
						trip
							? localOrUtcTimestampToLocalDate(
									trip.start_date_local,
									trip.end_date
								)
							: new Date(),
						"day"
					)
		)

		const duration = durationIso
			? {
					iso: durationIso,
					name: createDuration(durationIso).asMinutes() + " Mins",
				}
			: undefined

		return {
			pickup: pickup_date,
			pickup_time: canAddDuration(pickup_date)
				? formatDate(pickup_date, "HH:mm")
				: undefined,
			use_hotel_as_pickup: useHotelAsPickup ?? null,
			use_hotel_as_drop: useHotelAsDrop ?? null,
			pick_up_location: pickupLocation ?? null,
			drop_location: dropLocation ?? null,
			duration: duration,
			remarks: remarks || "",
			supplier_remarks: supplierRemarks || "",
			customer_remarks: customerRemarks || "",
			/** 
			has_same_customer_and_supplier_remarks = true if there will be remarks
			has_same_customer_and_supplier_remarks = false if there will be customer remarks or supplier remarks or if there will be not be any type of remarks
			**/
			are_remarks_separated:
				!hasSameCustomerAndSupplierRemarks &&
				Boolean(customerRemarks || supplierRemarks),
			comments: comments || "",
			has_cab_time_variation: hasCabTimeVariation ? 1 : 0,
			cabs: (cabs || initialCabs).map((c) => ({
				...c,
				confirmation_details: c.confirmation_details || "",
				already_booked: c.booked,
				members: (c.members || []).map((m) => ({
					...m,
					member_id: m.member_id,
					id:
						m.member_type === "tourists"
							? `tourist-${m.member_id}`
							: `group-${m.member_id}`,
				})),
				primary_contact: c.tourist ? c.tourist.id : null,
				all_tourists: c.tourists,
				tourists:
					c.tourists?.map((t) => ({
						id: t.id,
						remarks: t.booking_tourist?.remarks
							? t.booking_tourist?.remarks.trim()
							: null,
					})) || undefined,
				remarks: c.remarks || "",
				customer_remarks: c.customer_remarks || "",
				supplier_remarks: c.supplier_remarks || "",
				comments: c.comments || "",
				are_remarks_separated:
					!c.has_same_customer_and_supplier_remarks &&
					Boolean(c.customer_remarks || c.supplier_remarks),
				pickup_time:
					hasCabTimeVariation && c.pickup_time_local
						? formatDate(parseDate(c.pickup_time_local, "HH:mm"), "HH:mm") ||
							undefined
						: undefined,
				use_hotel_as_pickup: hasCabTimeVariation
					? (c.use_hotel_as_pickup ?? null)
					: null,
				use_hotel_as_drop: hasCabTimeVariation
					? (c.use_hotel_as_drop ?? null)
					: null,
				pick_up_location: hasCabTimeVariation
					? c.pick_up_location || null
					: null,
				drop_location: hasCabTimeVariation ? c.drop_location || null : null,
				duration: hasCabTimeVariation
					? c.duration_iso
						? {
								iso: c.duration_iso,
								name: createDuration(c.duration_iso).asMinutes() + " Mins",
							}
						: undefined
					: undefined,
			})) as undefined | typeof initialCabs,
			transport_service_location: transportService?.locations || null,
			transport_service: transportService
				? {
						...transportService,
						label: transportService.service,
					}
				: undefined,
			trip,
			members: (cabScheduleMembers || []).map((m) => ({
				...m,
				member_id: m.member_id,
				id:
					m.member_type === "tourists"
						? `tourist-${m.member_id}`
						: `group-${m.member_id}`,
			})),
			tourist: cabScheduleTourist,
			tourists:
				cabScheduleTourists?.map((t) => ({
					id: t.id,
					remarks: t.booking_tourist?.remarks
						? t.booking_tourist?.remarks.trim()
						: null,
				})) || undefined,
			all_tourists: cabScheduleTourists,
			primary_contact: cabScheduleTourist ? cabScheduleTourist.id : null,
		}
	})
	return (
		<Form<ScheduleFormData>
			initialValues={initialValues}
			validate={validationSchema}
			onSubmit={withServerErrors(async (values) => {
				if (values.transport_service) {
					const closeData = getClosedDates(
						values.transport_service,
						[new Date(values.pickup)],
						values.transport_service.service
					)
					if (Object.keys(closeData).length) {
						const alertHeader = "Some of the activities/services are closed. \n"
						const alertMessage = showClosedServicesAlert(closeData)
						const alertFooter =
							"\n Please update the activities/services before bookings."
						alert(alertHeader + alertMessage + alertFooter)
						return
					}
				}
				const start_date_local = startOf(values.pickup, "day")
				const start_time_local = values.pickup_time
					? parseDate(values.pickup_time, config.timeFormat)
					: undefined
				const start_date_time_local = start_time_local
					? setTimeFrom(start_date_local, start_time_local)
					: addUnit(start_date_local, 1, "minute")

				const duration = values.duration ? values.duration : undefined

				const parseDuration = duration
					? createDuration(duration.iso)
					: undefined

				let end_date_time_local =
					parseDuration && start_time_local
						? addUnit(
								start_date_time_local,
								Number(parseDuration.asMinutes()),
								"minutes"
							)
						: clone(start_date_time_local) // same as start_date_time_local

				// Process cabs and calculate their dropoff times
				const processedCabs = values.cabs
					? values.cabs
							.filter((c): c is Required<typeof c, "cab_type"> =>
								Boolean(c.cab_type)
							)
							.map((c) => {
								const hasTime = Boolean(c.pickup_time)
								const hasDuration = Boolean(c.duration?.iso)
								const pickupDateTime = c.pickup_time
									? parseDate(c.pickup_time, config.timeFormat)
									: null
								const durationMinutes =
									hasDuration && c.duration
										? Number(createDuration(c.duration.iso).asMinutes())
										: null

								// Combine pickup date and time to get full pickup datetime
								const fullPickupDateTime =
									pickupDateTime && start_date_time_local
										? parseDate(
												`${formatDate(start_date_time_local, "YYYY-MM-DD")} ${formatDate(pickupDateTime, config.timeFormat)}`,
												`YYYY-MM-DD ${config.timeFormat}`
											)
										: null

								const dropoffDateTime =
									fullPickupDateTime && durationMinutes
										? addUnit(fullPickupDateTime, durationMinutes, "minutes")
										: null

								const cabMemberTouristId: number[] = c.members
									? c.members.flatMap((m: TScheduledCabMember) => {
											if (m.member_type === "tourists") {
												const member = m as TScheduledCabMember<TTourist>
												return [member.member_id]
											} else if (m.member_type === "tourist_groups") {
												const member = m as TScheduledCabMember<TTouristGroup>
												return member.member.members.map((m) => m.tourist.id)
											}
											return []
										})
									: []

								const useHotelAsPickupForCab = values.has_cab_time_variation
									? c.use_hotel_as_pickup
									: undefined
								const useHotelAsDropForCab = values.has_cab_time_variation
									? c.use_hotel_as_drop
									: undefined
								const cabPickupLocation =
									values.has_cab_time_variation &&
									c.pick_up_location &&
									!useHotelAsPickupForCab
										? extractLocationString(c.pick_up_location)
										: undefined
								const cabDropLocation =
									values.has_cab_time_variation &&
									c.drop_location &&
									!useHotelAsDropForCab
										? extractLocationString(c.drop_location)
										: undefined
								return {
									dropoffDateTime, // Keep this for comparison
									data: {
										id: c.id,
										cab_type: c.cab_type.name,
										transport_service_provider:
											c.transport_service_provider?.id,
										driver: c.driver?.id,
										cab: c.cab?.id,
										booked_price: c.booked_price,
										given_price: c.given_price,
										calculated_price: c.calculated_price,
										booked: Number(c.booked),
										currency: c.currency,
										confirmation_details: c.confirmation_details,
										// If the cab type don't have a display name. e.g. [[cab-type-name]], don't save the remarks
										remarks:
											c.cab_type.display_name && !c.are_remarks_separated
												? c.remarks || null
												: null,
										customer_remarks:
											c.cab_type.display_name && c.are_remarks_separated
												? c.customer_remarks || null
												: null,
										supplier_remarks:
											c.cab_type.display_name && c.are_remarks_separated
												? c.supplier_remarks || null
												: null,
										comments: c.comments || null,
										pickup_time_local:
											pickupDateTime && values.has_cab_time_variation
												? formatDate(pickupDateTime, config.timeFormat)
												: null,
										pickup_date_local:
											hasTime && values.has_cab_time_variation
												? formatDate(start_date_time_local, "YYYY-MM-DD")
												: null,
										use_hotel_as_pickup: useHotelAsPickupForCab,
										use_hotel_as_drop: useHotelAsDropForCab,
										pick_up_location: cabPickupLocation,
										drop_location: cabDropLocation,
										duration:
											values.has_cab_time_variation && c.duration?.iso
												? c.duration.iso
												: null,
										dropoff_time_local:
											dropoffDateTime && values.has_cab_time_variation
												? formatDate(dropoffDateTime, config.timeFormat)
												: null,
										dropoff_date_local:
											dropoffDateTime && values.has_cab_time_variation
												? formatDate(dropoffDateTime, "YYYY-MM-DD")
												: null,
										members: (c.members || []).length
											? (c.members || []).map((m) => ({
													type: m.member_type,
													id: m.member_id,
												}))
											: undefined,
										primary_tourist: (c.members || []).length
											? c.primary_contact
											: null,
										tourists:
											c.tourists
												?.map((t) => ({
													id: t.id,
													remarks: t.remarks ? t.remarks.trim() : null,
												}))
												.filter((t) =>
													cabMemberTouristId.find((cmtId) => cmtId === t.id)
												) || undefined,
									},
								}
							})
					: []

				// Find the maximum dropoff datetime among all cabs
				const maxDropoffDateTime = processedCabs.reduce(
					(max, cab) => {
						if (!cab.dropoffDateTime) return max
						if (!max) return cab.dropoffDateTime
						return cab.dropoffDateTime > max ? cab.dropoffDateTime : max
					},
					null as Date | null
				)

				// Update service end date if any cab dropoff extends beyond it
				if (maxDropoffDateTime && maxDropoffDateTime > end_date_time_local) {
					end_date_time_local = maxDropoffDateTime
				}
				const memberTouristId: number[] = values?.members
					? Array.from(
							new Set(
								values.members.flatMap((m) => {
									if (m.member_type === "tourists") {
										return [m.member_id]
									} else if (m.member_type === "tourist_groups") {
										const group = m.member as TTouristGroup
										return group.members.map((gm) => gm.tourist.id)
									}
									return []
								})
							)
						)
					: []
				const payload = {
					start_date: dateToUTCString(start_date_time_local),
					start_date_local: formatDate(start_date_time_local, "YYYY-MM-DD"),
					start_time_local:
						start_time_local && !values.has_cab_time_variation
							? formatDate(start_date_time_local, config.timeFormat)
							: undefined,
					duration: !values.has_cab_time_variation ? duration?.iso : undefined,
					end_date: dateToUTCString(end_date_time_local),
					end_date_local: formatDate(end_date_time_local, "YYYY-MM-DD"),
					end_time_local:
						parseDuration && start_time_local && !values.has_cab_time_variation
							? formatDate(end_date_time_local, config.timeFormat)
							: undefined,
					use_hotel_as_pickup: !values.has_cab_time_variation
						? values.use_hotel_as_pickup
						: undefined,
					use_hotel_as_drop: !values.has_cab_time_variation
						? values.use_hotel_as_drop
						: undefined,
					pick_up_location:
						!values.has_cab_time_variation && !values.use_hotel_as_pickup
							? extractLocationString(values.pick_up_location)
							: undefined,
					drop_location:
						!values.has_cab_time_variation && !values.use_hotel_as_drop
							? extractLocationString(values.drop_location)
							: undefined,
					transport_service_id: values.transport_service
						? values.transport_service.id
						: null,
					remarks: values.are_remarks_separated ? undefined : values.remarks,
					supplier_remarks: values.are_remarks_separated
						? values.supplier_remarks
						: undefined,
					customer_remarks: values.are_remarks_separated
						? values.customer_remarks
						: undefined,
					comments: values.comments,

					has_cab_time_variation: values.has_cab_time_variation || 0,
					cabs: processedCabs.map((c) => c.data),
					trip_id: values.trip ? values.trip.id : null,
					members: (values?.members || []).length
						? (values?.members || []).map((m) => ({
								type: m.member_type,
								id: m.member_id,
							}))
						: undefined,
					primary_tourist: (values.members || []).length
						? values.primary_contact
						: null,
					tourists:
						values.tourists
							?.map((t) => ({
								id: t.id,
								remarks: t.remarks ? t.remarks.trim() : null,
							}))
							.filter((t) => memberTouristId.find((mtId) => mtId === t.id)) ||
						undefined,
				}
				await onSubmit(payload)
				if (onSuccess) {
					onSuccess()
				} else {
					onCancel?.()
				}
			})}
			subscription={{ submitting: true }}
			mutators={{ ...arrayMutators }}
		>
			{({ submitting, form, handleSubmit }) => (
				<form noValidate id="cab_schedule_form" onSubmit={handleSubmit}>
					<ScheduleFormDetails
						handleServiceLocationChange={handleServiceLocationChange}
						initialValues={initialValues}
						readOnlyTrip={readOnlyTrip}
						serviceOptions={serviceOptions}
						trip={trip}
					/>
					<Divider />
					<Grid gap="4">
						<Col sm={12} md={{ offset: 3, span: 8 }}>
							<Stack gap="4" paddingLeft={{ md: "4" }}>
								<SubmissionError />
								<Inline gap="4">
									<Button type="submit" disabled={submitting} size="lg">
										{submitting ? "Please wait..." : "Save Details"}
									</Button>
									{onCancel ? (
										<Button
											disabled={submitting}
											onClick={() => onCancel()}
											size="lg"
											level="tertiary"
										>
											Cancel
										</Button>
									) : null}
								</Inline>
							</Stack>
						</Col>
					</Grid>
				</form>
			)}
		</Form>
	)
}

interface DatePickupFieldProps {
	trip?: ITrip
	name?: string
	label?: string
}

export function DatePickupField({
	trip,
	name = "pickup",
	label = "Date",
}: DatePickupFieldProps) {
	// Memoize base booking dates from trip
	const baseBookingDates = useMemo(() => {
		if (!trip || !trip.start_date || !trip.end_date) return []
		const startDate = new Date(
			localOrUtcTimestampToLocalDate(trip.start_date_local, trip.start_date)
		)
		const endDate = new Date(
			localOrUtcTimestampToLocalDate(trip.end_date_local, trip.end_date)
		)
		return createBookingDateSelectionOptionsFromStartAndEndDates(
			startDate,
			endDate
		).map((item) => ({
			id: item.id,
			name: item.name,
			value: formatDate(item.value, "YYYY-MM-DD"),
		}))
	}, [trip])

	// Get current value for the field
	const { value: currentPickupValue } = useFieldValue<
		string | Date | undefined
	>(name)

	// Memoize bookingDates, always including current value if not present
	const bookingDates = useMemo(() => {
		const result = [...baseBookingDates]
		if (currentPickupValue) {
			const currentValueString =
				typeof currentPickupValue === "string"
					? currentPickupValue
					: formatDate(currentPickupValue, "YYYY-MM-DD")
			const isInBaseDates = baseBookingDates.find(
				(d) => d.value === currentValueString
			)
			if (!isInBaseDates) {
				const currentValueDate = new Date(currentPickupValue)
				if (!isNaN(currentValueDate.getTime())) {
					const dayOfWeek = formatDate(currentValueDate, "ddd")
					const month = formatDate(currentValueDate, "MMM")
					const date = formatDate(currentValueDate, "D")
					result.unshift({
						id: currentValueString,
						name: `Current: ${dayOfWeek} ${date} ${month}`,
						value: currentValueString,
					})
				}
			}
		}
		return result
	}, [currentPickupValue, baseBookingDates])

	return (
		<FieldGroup<string> name={name} label={label}>
			{({ input }) => {
				const { value } = input
				return (
					<Select
						{...input}
						options={bookingDates}
						searchable={false}
						placeholder="Select day..."
						required
						multiple={false}
						value={
							value && bookingDates
								? bookingDates.find((d: { value: string }) => {
										const currentValueString =
											typeof value === "string"
												? value
												: formatDate(value, "YYYY-MM-DD")
										return d.value === currentValueString
									})
								: undefined
						}
						onChange={(selectedDay: (typeof bookingDates)[0] | undefined) => {
							input.onChange(selectedDay ? selectedDay.value : undefined)
						}}
					/>
				)
			}}
		</FieldGroup>
	)
}

function canAddDuration(pickup: Date) {
	return formatDate(pickup, "HH:mm") !== "00:01"
}

function ExistingScheduledCabsForDriver({
	scheduledCabFieldName,
	tripId,
}: {
	scheduledCabFieldName: string
	tripId?: number | string
}) {
	const { value: pickupDate } = useFieldValue<Date | string | undefined>(
		"pickup"
	)
	const { value: pickupTime } = useFieldValue<string | undefined>("pickup_time")
	const { value: duration } = useFieldValue<
		| {
				iso: string
				name: string
		  }
		| undefined
	>("duration")
	const pickupDateStartOfDay = useMemo(() => {
		if (!pickupDate) return undefined
		return startOf(pickupDate, "day")
	}, [pickupDate])
	const { value: supplier } = useFieldValue<ISupplier | undefined>(
		`${scheduledCabFieldName}.transport_service_provider`
	)
	const { value: existingId } = useFieldValue<number | undefined>(
		`${scheduledCabFieldName}.id`
	)
	const { value: driver } = useFieldValue<TDriver>(
		`${scheduledCabFieldName}.driver`
	)
	const { value: cab } = useFieldValue<ICab>(`${scheduledCabFieldName}.cab`)

	if (!pickupDateStartOfDay || !supplier) {
		return null
	}

	return (
		<WarnForExistingScheduledCabsForSupplier
			tripId={tripId}
			startDate={pickupDateStartOfDay}
			startTime={pickupTime ? parseDate(pickupTime, "HH:mm") : undefined}
			scheduledCabId={existingId}
			supplier={supplier}
			driver={driver}
			duration={duration ? createDuration(duration.iso).asMinutes() : undefined}
			cab={cab}
		/>
	)
}

function CabPriceCalculator({ cabFieldName }: { cabFieldName: string }) {
	const { value: transportService } = useFieldValue<
		ITransportService | undefined
	>("transport_service")
	const form = useForm()
	const xhr = useXHR()
	const { value: cabType } = useFieldValue<ICabType | undefined>(
		`${cabFieldName}.cab_type`
	)
	const { value: pickupDate } = useFieldValue<Date | undefined>("pickup")
	const { value: duration } = useFieldValue<
		| {
				iso: string
				name: string
		  }
		| undefined
	>("duration")
	const abortController = useRef<AbortController>()
	const previousValues = useRef({
		cabTypeId: cabType?.id,
		pickupDate: pickupDate,
		transportServiceId: transportService?.id,
	})

	const hasCabTypeChanged = cabType?.id !== previousValues.current.cabTypeId

	const hasPickupDateChanged =
		previousValues.current.pickupDate !== undefined &&
		pickupDate !== undefined &&
		!isSame(previousValues.current.pickupDate, pickupDate, "day")

	const hasTransportServiceChanged =
		transportService?.id !== previousValues.current.transportServiceId

	const hasServicesChanged =
		hasCabTypeChanged || hasPickupDateChanged || hasTransportServiceChanged

	// Update previous values after change detection
	useEffect(() => {
		previousValues.current = {
			cabTypeId: cabType?.id,
			pickupDate: pickupDate,
			transportServiceId: transportService?.id,
		}
	}, [cabType?.id, pickupDate, transportService?.id])

	useEffect(() => {
		// abort any pending requests
		abortController.current = new AbortController()

		if (!transportService || !cabType || !pickupDate) {
			abortController.current?.abort()
			return () => undefined
		}

		// Fetch cab price from API
		const fetchCabPrice = async () => {
			try {
				form.change(`${cabFieldName}.fetching_price`, true)

				const response = await xhr.get<{
					data: {
						price: number | null
						per_quantity_price: number | null
						currency: string | null
					}
				}>(`/cab-prices/calculate`, {
					params: {
						date: dateToUTCString(pickupDate),
						date_local: formatDate(pickupDate, "YYYY-MM-DD"),
						transport_service_id: transportService.id,
						cab_type: cabType.name,
						no_of_cabs: 1,
						is_booking: 1,
					},
					signal: abortController.current?.signal,
				})

				const priceData = response.data.data

				// Update form with calculated price if available
				if (priceData.price !== undefined) {
					form.batch(() => {
						// Update calculated_price
						if (priceData.price !== null) {
							form.change(`${cabFieldName}.calculated_price`, priceData.price)
						} else {
							form.change(`${cabFieldName}.calculated_price`, null)
						}

						// Update booked_price only when services are explicitly changed (Cab Type, Date, Transport Service)
						if (hasServicesChanged) {
							if (priceData.price !== null) {
								form.change(`${cabFieldName}.booked_price`, priceData.price)
							} else {
								// Set booked_price to 0 when services have changed
								form.change(`${cabFieldName}.booked_price`, "")
							}
						}

						// Update currency if available
						if (priceData.currency) {
							form.change(`${cabFieldName}.currency`, priceData.currency)
						}
					})
				}
			} catch (error) {
				if (error instanceof Error && error.name !== "AbortError") {
					console.error("Error fetching cab price:", error)
				}
			} finally {
				form.change(`${cabFieldName}.fetching_price`, false)
			}
		}

		// Debounce the API call
		const timeoutId = setTimeout(fetchCabPrice, 500)

		return () => {
			clearTimeout(timeoutId)
			abortController.current?.abort()
		}
	}, [
		transportService,
		cabType,
		pickupDate,
		duration,
		cabFieldName,
		form,
		xhr,
		hasServicesChanged,
	])

	return null
}

function ScheduleFormDetails({
	readOnlyTrip,
	trip,
	serviceOptions,
	handleServiceLocationChange,
	initialValues,
}: {
	readOnlyTrip: boolean | undefined
	trip: ITrip | undefined
	serviceOptions: (ITransportService & {
		label: string
	})[]
	handleServiceLocationChange: (
		value: TTransportServiceLocation | null,
		form: FormApi<ScheduleFormData>
	) => void
	initialValues: ScheduleFormData
}) {
	const { value: allTourists } =
		useFieldValue<ScheduleFormData["all_tourists"]>("all_tourists")
	const [showTouristsInput, setShowTouristsInput] = useState(
		(allTourists || []).length ? true : false
	)
	const form = useForm<ScheduleFormData>()
	const { hasPermission } = useCheckPermissions()
	const allCurrencies = useCurrenciesCodesOfTenant()
	return (
		<>
			<Grid>
				<Col sm={12} md={3}>
					<Stack gap="1">
						<Heading as="h3">Trip and Service Details</Heading>
						<Text color="muted">
							Please provide transport service with and pickup and drop details.
						</Text>
					</Stack>
				</Col>
				<Col sm={12} md={9}>
					<Stack gap="4">
						{readOnlyTrip && trip ? (
							<Inline gap="6" flexWrap="wrap" paddingLeft="4">
								<Stack gap="px">
									<Text fontSize="sm" color="muted" fontWeight="semibold">
										Destinations
									</Text>
									<Text>{trip.destinations.map((t) => t.name).join(", ")}</Text>
								</Stack>
								<Stack
									gap="px"
									borderLeftWidth="1"
									borderColor="muted"
									paddingLeft="4"
								>
									<Text fontSize="sm" color="muted" fontWeight="semibold">
										Trip Start Date
									</Text>
									<Text>
										<Time
											timestamp={trip.start_date}
											localTimestamp={trip.start_date_local}
										/>
									</Text>
								</Stack>
								<Stack
									gap="px"
									borderLeftWidth="1"
									borderColor="muted"
									paddingLeft="4"
								>
									<Text fontSize="sm" color="muted" fontWeight="semibold">
										Trip End Date
									</Text>
									<Text>
										<Time
											timestamp={trip.end_date}
											localTimestamp={trip.end_date_local}
										/>
									</Text>
								</Stack>
								<Stack
									gap="px"
									borderLeftWidth="1"
									borderColor="muted"
									paddingLeft="4"
								>
									<Text fontSize="sm" color="muted" fontWeight="semibold">
										Pax
									</Text>
									<Text>{trip.pax_string}</Text>
								</Stack>
								<Stack
									gap="px"
									borderLeftWidth="1"
									borderColor="muted"
									paddingLeft="4"
								>
									<Text fontSize="sm" color="muted" fontWeight="semibold">
										Cabs
									</Text>
									<GetFieldValue<NewScheduleRequestData["cabs"]> name="cabs">
										{({ value: cabs }) => {
											const displayNames = (cabs || []).map(
												(c) => c.cab_type?.display_name
											)
											const counts: Record<string, number> = {}
											displayNames.forEach((name) => {
												if (!name) return
												counts[name] = (counts[name] || 0) + 1
											})
											return (
												<Inline gap="1">
													{Object.entries(counts).map(
														([name, count], index) => (
															<Inline gap="1">
																<Text color="subtle">
																	{index > 0 ? "•" : ""}
																</Text>{" "}
																{count} {name}
															</Inline>
														)
													)}
												</Inline>
											)
										}}
									</GetFieldValue>
								</Stack>
							</Inline>
						) : (
							<SelectField
								name="trip"
								select={SelectTrips}
								label="Select Trip"
								required
							/>
						)}
						<Grid style={{ rowGap: "1rem" }}>
							<GetFieldValue<NewScheduleRequestData["trip"]> name="trip">
								{({ value: trip }) => (
									<>
										<Col xs={12} md={6} lg={3}>
											<DatePickupField
												trip={trip as ITrip}
												name="pickup"
												label="Date"
											/>
										</Col>
										<Col xs={12} md={6} lg={5}>
											<SelectField
												select={SelectTransportServiceLocations}
												name="transport_service_location"
												creatable
												tripDestinations={trip?.destinations}
												label="Service Locations"
												multiple={false}
												onChange={(value) => {
													handleServiceLocationChange(value, form)
												}}
											/>
										</Col>
										<Col xs={12} md={6} lg={4}>
											<SelectField
												select={Select}
												name="transport_service"
												label="Service Type"
												multiple={false}
												options={serviceOptions}
												labelKey="label"
												fullWidth
												help={
													<GetFieldValue<Date> name="pickup">
														{({ value: date }) => (
															<GetFieldValue<
																NewScheduleRequestData["transportService"]
															> name="transport_service">
																{({ value: service }) => (
																	<>
																		{isTicketTypeClosedOnDate(
																			{
																				closed_on_date_intervals:
																					service?.closed_on_date_intervals,
																				closed_on_days_of_week:
																					service?.closed_on_days_of_week,
																			},
																			date
																		) ? (
																			<Box maxWidth={"sm"}>
																				<Alert
																					status="error"
																					title="Service Closed."
																				>
																					<Text>{`Closed on ${formatDate(date, "dddd, DD MMM")}.`}</Text>
																				</Alert>
																			</Box>
																		) : null}
																	</>
																)}
															</GetFieldValue>
														)}
													</GetFieldValue>
												}
												onChange={(value) => {
													form.change("transport_service", value as never)
													form.batch(() => {
														form.change(
															`pick_up_location` as never,
															value?.pick_up_locations?.at(0) as never
														)
														form.change(
															`drop_location` as never,
															value?.drop_locations?.at(0) as never
														)
														form.change(
															`use_hotel_as_pickup` as never,
															value?.set_hotel_as_default_pickup as never
														)
														form.change(
															`use_hotel_as_drop` as never,
															value?.set_hotel_as_default_drop as never
														)
													})
												}}
											/>
										</Col>
										<Col xs={12}>
											<RemarksAndTouristAssignment
												allTourists={allTourists}
												setShowTouristsInput={setShowTouristsInput}
												showTouristsInput={showTouristsInput}
												trip={trip}
											/>
										</Col>
									</>
								)}
							</GetFieldValue>
						</Grid>
					</Stack>
				</Col>
			</Grid>
			<Divider />
			<Grid>
				<Col sm={12} md={3}>
					<Stack gap="1">
						<Heading as="h3">Timing</Heading>
						<Text color="muted">
							Please provide transport pickup and drop details.
						</Text>
					</Stack>
				</Col>
				<Col xs={12} md={9}>
					<Stack gap="4">
						<GetFieldValue<boolean> name="has_cab_time_variation">
							{({ value: hasCabWiseDetails }) => (
								<Grid
									style={{ rowGap: "1rem" }}
									opacity={hasCabWiseDetails ? "50" : undefined}
									pointerEvents={hasCabWiseDetails ? "none" : undefined}
								>
									<Col xs={12} md={6} lg={4}>
										<SelectField
											label="Pickup Time"
											secondaryLabel="24 hrs - optional"
											minWidth="auto"
											select={Select}
											name="pickup_time"
											clearable={true}
											placeholder="14:00"
											menuPlaceholder="Type time and select"
											creatable
											createOptionLabel={(query) => {
												let date
												try {
													date = parseDate(query, "HH:mm")
												} catch (e) {
													date = undefined
												}
												return date
													? `Set: ${formatDate(date, "HH:mm")} hrs`
													: "Invalid Time"
											}}
											closeOnSelect
											onCreateNew={(query) => {
												let date
												try {
													date = parseDate(query, "HH:mm")
												} catch (e) {
													date = undefined
												}
												return date ? formatDate(date, "HH:mm") : ""
											}}
										/>
									</Col>
									<GetFieldValue<Date | undefined> name="pickup">
										{({ value: pickupDate }) => (
											<GetFieldValue<string | undefined> name="pickup_time">
												{({ value: pickupTime }) => {
													const pickupDateTime =
														pickupDate && pickupTime
															? setTimeFrom(
																	pickupDate,
																	parseDate(pickupTime, "HH:mm")
																)
															: undefined
													return (
														<Col xs={12} md={6} lg={4}>
															<SelectField
																minWidth="auto"
																label="Duration (mins)"
																name="duration"
																secondaryLabel="optional"
																placeholder="e.g. 240"
																select={Select}
																creatable={true}
																createOptionLabel={(q) => q}
																onCreateNew={(query) => {
																	let durationISO = ""
																	try {
																		durationISO = createDuration(
																			query.replace(/[^\d]/g, ""),
																			"minutes"
																		).toISOString()
																	} catch (e) {
																		durationISO = ""
																	}
																	let duration:
																		| {
																				iso: string
																				name: string
																		  }
																		| undefined = undefined
																	if (durationISO) {
																		duration = {
																			iso: durationISO,
																			name:
																				createDuration(
																					durationISO
																				).asMinutes() + " Mins",
																		}
																	}
																	return duration
																}}
																help={
																	<GetFieldValue<
																		| {
																				iso: string
																				name: string
																		  }
																		| undefined
																	> name="duration">
																		{({ value }) => {
																			if (pickupDateTime && value) {
																				const parseDuration = createDuration(
																					value.iso
																				)

																				const drop = addUnit(
																					pickupDateTime,
																					parseDuration.asMinutes(),
																					"minutes"
																				)
																				return (
																					<Text
																						color={
																							!isSame(
																								drop,
																								pickupDateTime,
																								"day"
																							)
																								? "danger"
																								: undefined
																						}
																					>
																						End Date:{" "}
																						{formatDate(drop, "D MMM, HH:mm")} (
																						{humanizeDuration(
																							getDiff(
																								drop,
																								pickupDateTime,
																								"millisecond"
																							)
																						)}
																						)
																					</Text>
																				)
																			}
																			return null
																		}}
																	</GetFieldValue>
																}
															/>
														</Col>
													)
												}}
											</GetFieldValue>
										)}
									</GetFieldValue>
									<GetFieldValue<
										ITransportService | undefined
									> name="transport_service">
										{({ value: selectedTransportService }) => {
											return (
												<PickupDropLocationFields
													pickupLocations={
														selectedTransportService?.pick_up_locations || []
													}
													dropLocations={
														selectedTransportService?.drop_locations || []
													}
												/>
											)
										}}
									</GetFieldValue>
								</Grid>
							)}
						</GetFieldValue>
						<Inline
							paddingLeft="4"
							paddingTop="4"
							borderTopWidth="1"
							borderColor="muted"
						>
							<SwitchInputField
								name="has_cab_time_variation"
								label="Customized these Details Cabwise"
								onChange={(value) => {
									form.change("has_cab_time_variation", value as never)
									const cabs = form.getFieldState("cabs")?.value
									if (!value) {
										form.change(
											"cabs",
											cabs?.map((cab) => {
												return {
													...cab,
													pick_up_location: null,
													drop_location: null,
												}
											}) as never
										)
									}
								}}
							/>
						</Inline>
					</Stack>
				</Col>
			</Grid>
			<Divider />
			<Grid>
				<Col sm={12} md={3}>
					<Stack gap="1">
						<Heading as="h3">Cabs & Drivers</Heading>
						<Text color="muted">
							Please provide cab details along with drivers and service
							providers if booked.
						</Text>
					</Stack>
				</Col>
				<Col sm={12} md={9}>
					<FieldArray<
						Required<NewScheduleRequestData, "cabs">["cabs"][number]
					> name="cabs">
						{({ fields }) => (
							<>
								{fields.map((name, index) => (
									<Box display="flex" key={index}>
										{Number(fields.length) > 1 ? (
											<Box
												paddingX="2"
												paddingY="4"
												borderRightWidth="1"
												bgColor="inset"
												borderBottomWidth="1"
												borderLeftWidth="1"
												borderTopWidth={index === 0 ? "1" : undefined}
												fontWeight="semibold"
												style={{
													fontFeatureSettings: "tnum",
													fontVariantNumeric: "tabular-nums",
												}}
											>
												{index + 1}
											</Box>
										) : null}
										<Box
											flex="1"
											minWidth="0"
											borderBottomWidth="1"
											paddingY="4"
										>
											<Stack gap="4">
												<Grid style={{ rowGap: "1rem" }}>
													<Col xs={12}>
														<Inline
															justifyContent="between"
															alignItems="center"
														>
															<GetFieldValue<
																NewScheduleRequestData["trip"]
															> name="trip">
																{({ value: trip }) => (
																	<GetFieldValue<number | undefined>
																		name={`${name}.id`}
																	>
																		{({ value: existingId }) => (
																			<Inline gap={"2"}>
																				<SelectField
																					name={`${name}.cab_type`}
																					select={SelectCabTypes}
																					creatable
																					multiple={false}
																					label="Cab Type"
																					tripDestinations={trip?.destinations}
																					secondaryLabel={
																						existingId ? `#${existingId}` : null
																					}
																					required
																				/>
																				<Box paddingTop={"8"}>
																					<SelectFromCabTypeList
																						name={`${name}.cab_type`}
																						destinations={trip?.destinations}
																					>
																						{({ onOpen }) => (
																							<Button inline onClick={onOpen}>
																								<Icons.ArrowExpand />
																							</Button>
																						)}
																					</SelectFromCabTypeList>
																				</Box>
																			</Inline>
																		)}
																	</GetFieldValue>
																)}
															</GetFieldValue>
															<Dropdown alignRight>
																<Dropdown.ToggleButton
																	level="tertiary"
																	size="sm"
																>
																	<Icons.DotsVertical />
																</Dropdown.ToggleButton>
																<Dropdown.Menu>
																	<GetFieldValue<
																		Required<
																			NewScheduleRequestData,
																			"cabs"
																		>["cabs"][number]
																	>
																		name={name}
																	>
																		{({ value: cab }) => (
																			<Dropdown.MenuItem
																				onClick={() =>
																					fields.push({
																						...cab,
																						id: undefined,
																						confirmation_details:
																							cab.confirmation_details || "",
																						remarks: cab.remarks || "",
																						customer_remarks:
																							cab.customer_remarks || "",
																						supplier_remarks:
																							cab.supplier_remarks || "",
																						comments: cab.comments || "",
																						pickup_time:
																							cab.pickup_time || undefined,
																					})
																				}
																			>
																				<Icons.Duplicate /> Duplicate Cab
																			</Dropdown.MenuItem>
																		)}
																	</GetFieldValue>
																	{Number(fields.length) > 0 &&
																	index === Number(fields.length) - 1 ? (
																		<Dropdown.MenuItem
																			onClick={() =>
																				fields.push({
																					...(initialValues.cabs?.[0] || {}),
																					id: undefined,
																					cab_type: undefined,
																					driver: undefined,
																					cab: undefined,
																					booked: false,
																					confirmation_details: "",
																					booked_price: 0,
																					calculated_price: 0,
																					given_price: 0,
																					remarks: "",
																					customer_remarks: "",
																					supplier_remarks: "",
																					comments: "",
																					pickup_time: undefined,
																					members: [],
																					has_same_customer_and_supplier_remarks: false,
																				})
																			}
																		>
																			<Icons.Plus /> Add More Cabs
																		</Dropdown.MenuItem>
																	) : null}
																</Dropdown.Menu>
															</Dropdown>
														</Inline>
													</Col>
													<Col xs={12} sm={6} md={4}>
														<SelectField
															name={`${name}.transport_service_provider`}
															select={SelectTransporServiceProviders}
															label="Service Provider"
															creatable
															onChange={(
																value: ISupplier | undefined,
																_name
															) => {
																form.batch(() => {
																	form.change(_name as never, value as never)
																	if (value?.solo_driver) {
																		form.change(
																			`${name}.driver` as never,
																			value.drivers?.[0] as never
																		)
																	} else {
																		form.change(
																			`${name}.driver` as never,
																			undefined
																		)
																	}
																	form.change(`${name}.cab` as never, undefined)
																})
															}}
														/>
													</Col>
													<GetFieldValue<
														Required<
															NewScheduleRequestData,
															"cabs"
														>["cabs"][number]["transport_service_provider"]
													>
														name={`${name}.transport_service_provider`}
													>
														{({ value: transport_service_provider }) =>
															transport_service_provider ? (
																<>
																	{!transport_service_provider.solo_driver ? (
																		<Col xs={12} sm={6} md={4}>
																			<SelectField
																				name={`${name}.driver`}
																				select={SelectDriver}
																				label="Driver"
																				transportServiceProvider={
																					transport_service_provider
																				}
																				options={
																					transport_service_provider.drivers ||
																					[]
																				}
																				creatable
																			/>
																		</Col>
																	) : null}
																	<Col xs={12} sm={6} md={4}>
																		<SelectField
																			name={`${name}.cab`}
																			select={SelectCabs}
																			label="Cab Details"
																			options={
																				transport_service_provider.cabs || []
																			}
																			creatable
																		/>
																	</Col>
																</>
															) : null
														}
													</GetFieldValue>
												</Grid>
												<ExistingScheduledCabsForDriver
													scheduledCabFieldName={name}
													tripId={trip?.id}
												/>
												<CabPriceCalculator cabFieldName={name} />
												<Grid style={{ rowGap: "1rem" }}>
													<Col xs={12} sm={4}>
														<CabBookedPriceField
															name={name}
															allCurrencies={allCurrencies}
															hasPermission={hasPermission}
														/>
													</Col>
												</Grid>
												<GetFieldValue<boolean> name="has_cab_time_variation">
													{({ value: has_cab_time_variation }) => (
														<>
															{has_cab_time_variation ? (
																<Grid style={{ rowGap: "1rem" }}>
																	<Col xs={12} md={6} lg={4}>
																		<SelectField
																			label="Pickup Time"
																			secondaryLabel="24 hrs - optional"
																			select={Select}
																			name={`${name}.pickup_time`}
																			clearable={true}
																			placeholder="14:00"
																			menuPlaceholder="Type time and select"
																			creatable
																			createOptionLabel={(query) => {
																				let date
																				try {
																					date = parseDate(query, "HH:mm")
																				} catch (e) {
																					date = undefined
																				}
																				return date
																					? `Set: ${formatDate(date, "HH:mm")} hrs`
																					: "Invalid Time"
																			}}
																			closeOnSelect
																			onCreateNew={(query) => {
																				let date
																				try {
																					date = parseDate(query, "HH:mm")
																				} catch (e) {
																					date = undefined
																				}
																				return date
																					? formatDate(date, "HH:mm")
																					: ""
																			}}
																		/>
																	</Col>
																	<GetFieldValue<Date | undefined>
																		name={`${name}.pickup`}
																	>
																		{({ value: pickupDate }) => (
																			<GetFieldValue<string | undefined>
																				name={`${name}.pickup_time`}
																			>
																				{({ value: pickupTime }) => {
																					const pickupDateTime =
																						pickupDate && pickupTime
																							? setTimeFrom(
																									pickupDate,
																									parseDate(pickupTime, "HH:mm")
																								)
																							: undefined
																					return (
																						<Col xs={12} md={6} lg={4}>
																							<SelectField
																								minWidth="auto"
																								label="Duration (mins)"
																								name={`${name}.duration`}
																								secondaryLabel="optional"
																								placeholder="e.g. 240"
																								select={Select}
																								creatable={true}
																								createOptionLabel={(q) => q}
																								onCreateNew={(query) => {
																									let durationISO = ""
																									try {
																										durationISO =
																											createDuration(
																												query.replace(
																													/[^\d]/g,
																													""
																												),
																												"minutes"
																											).toISOString()
																									} catch (e) {
																										durationISO = ""
																									}
																									let duration:
																										| {
																												iso: string
																												name: string
																										  }
																										| undefined = undefined
																									if (durationISO) {
																										duration = {
																											iso: durationISO,
																											name:
																												createDuration(
																													durationISO
																												).asMinutes() + " Mins",
																										}
																									}
																									return duration
																								}}
																								help={
																									<GetFieldValue<
																										| {
																												iso: string
																												name: string
																										  }
																										| undefined
																									>
																										name={`${name}.duration`}
																									>
																										{({ value }) => {
																											if (
																												pickupDateTime &&
																												value
																											) {
																												const parseDuration =
																													createDuration(
																														value.iso
																													)

																												const drop = addUnit(
																													pickupDateTime,
																													parseDuration.asMinutes(),
																													"minutes"
																												)
																												return (
																													<Text
																														color={
																															!isSame(
																																drop,
																																pickupDateTime,
																																"day"
																															)
																																? "danger"
																																: undefined
																														}
																													>
																														End Date:{" "}
																														{formatDate(
																															drop,
																															"D MMM, HH:mm"
																														)}{" "}
																														(
																														{humanizeDuration(
																															getDiff(
																																drop,
																																pickupDateTime,
																																"millisecond"
																															)
																														)}
																														)
																													</Text>
																												)
																											}
																											return null
																										}}
																									</GetFieldValue>
																								}
																							/>
																						</Col>
																					)
																				}}
																			</GetFieldValue>
																		)}
																	</GetFieldValue>
																	<GetFieldValue<
																		ITransportService | undefined
																	> name="transport_service">
																		{({ value: selectedTransportService }) => (
																			<PickupDropLocationFields
																				namePrefix={name}
																				pickupLocations={
																					selectedTransportService?.pick_up_locations ||
																					[]
																				}
																				dropLocations={
																					selectedTransportService?.drop_locations ||
																					[]
																				}
																			/>
																		)}
																	</GetFieldValue>
																</Grid>
															) : null}
														</>
													)}
												</GetFieldValue>

												<Box
													paddingY="2"
													paddingX="4"
													borderWidth="1"
													rounded="md"
													bgColor="subtle"
													marginX="4"
												>
													<SwitchInputField
														name={`${name}.booked`}
														label="Confirmed by Supplier"
														help={
															"Please select it when the cab is booked. After booking, instalments will be created for this booking."
														}
													/>
													<GetFieldValue<
														Required<
															NewScheduleRequestData,
															"cabs"
														>["cabs"][number]["booked"]
													>
														name={`${name}.booked`}
													>
														{({ value: booked }) => (
															<GetFieldValue<
																Required<
																	NewScheduleRequestData,
																	"cabs"
																>["cabs"][number]["transport_service_provider"]
															>
																name={`${name}.transport_service_provider`}
															>
																{({ value: supplier }) =>
																	supplier && booked ? (
																		<Stack marginLeft="4">
																			<Divider sm />
																			<TransportPaymentPreferencesForm
																				supplierId={supplier.id}
																			/>
																		</Stack>
																	) : null
																}
															</GetFieldValue>
														)}
													</GetFieldValue>
												</Box>
												<Box maxWidth="lg" paddingLeft="4">
													<TextAreaInputField
														name={`${name}.confirmation_details`}
														label="Confirmation Details"
														help="Please enter any confirmation details after booking e.g. ticket numbers / PNR or links."
														placeholder="Enter details..."
													/>
												</Box>

												<CabWiseRemarkAndTouristAssignment
													name={name}
													trip={trip}
													showTouristsInput={showTouristsInput}
													setShowTouristsInput={setShowTouristsInput}
												/>
											</Stack>
											<Box paddingTop="2" paddingLeft="4">
												{Number(fields.length) > 1 ? (
													<Button
														onClick={() => fields.remove(index)}
														title="Remove"
														size="sm"
														level="tertiary"
													>
														<Icons.Cancel /> Remove
													</Button>
												) : null}
											</Box>
										</Box>
									</Box>
								))}
							</>
						)}
					</FieldArray>
					<GetFieldValue<NewScheduleRequestData["cabs"]> name="cabs">
						{({ value: cabs }) => {
							return (
								<CabCapacityWarningMessage
									children={trip?.children}
									messageType="withPax"
									cabs={cabs}
									noOfAdults={trip?.no_of_adults ?? 0}
								/>
							)
						}}
					</GetFieldValue>
				</Col>
			</Grid>
		</>
	)
}

export function RemarksAndTouristAssignment({
	showTouristsInput,
	setShowTouristsInput,
	trip,
	allTourists,
}: {
	showTouristsInput: boolean
	setShowTouristsInput: (value: boolean) => void
	trip: NewScheduleRequestData["trip"]
	allTourists: ScheduleFormData["all_tourists"]
}) {
	const form = useForm<ScheduleFormData>()
	const { value: custometRemarksValue } = useFieldValue<string | undefined>(
		"customer_remarks"
	)
	const { value: supplierRemarksValue } = useFieldValue<string | undefined>(
		"supplier_remarks"
	)
	const { value: remarksValue } = useFieldValue<string | undefined>("remarks")
	const { value: commentsValue } = useFieldValue<string | undefined>("comments")
	const { value: areRemarksSeparated } = useFieldValue<boolean>(
		"are_remarks_separated"
	)
	const [showRemarksInput, setShowRemarksInput] =
		useState<boolean>(!!remarksValue)

	const [showCustomerRemarksInput, setShowCustomerRemarksInput] =
		useState<boolean>(!!custometRemarksValue)

	const [showSupplierRemarksInput, setShowSupplierRemarksInput] =
		useState<boolean>(!!supplierRemarksValue)

	const [showCommentsInput, setShowCommentsInput] =
		useState<boolean>(!!commentsValue)
	return (
		<Inline
			gap="4"
			collapseBelow={
				showCustomerRemarksInput ||
				showSupplierRemarksInput ||
				showRemarksInput ||
				showCommentsInput ||
				showTouristsInput
					? "xl"
					: undefined
			}
			flexWrap={"wrap"}
		>
			<Stack gap={"2"}>
				{areRemarksSeparated ? (
					<Inline
						gap={"4"}
						collapseBelow={
							showCustomerRemarksInput || showSupplierRemarksInput
								? "xl"
								: undefined
						}
						flexWrap={"wrap"}
					>
						{showCustomerRemarksInput ? (
							<Box maxWidth={"lg"}>
								<MarkdownInputField
									name="customer_remarks"
									autoFocus={custometRemarksValue ? false : true}
									label="Remarks for Customer"
									secondaryLabel="optional"
									placeholder="Any notes/remarks that should be shared with Customer..."
								/>
							</Box>
						) : null}

						{showSupplierRemarksInput ? (
							<Box maxWidth={"lg"}>
								<MarkdownInputField
									name="supplier_remarks"
									autoFocus={supplierRemarksValue ? false : true}
									label="Remarks for Supplier"
									secondaryLabel="optional"
									placeholder="Any notes/remarks that should be shared with Supplier..."
								/>
							</Box>
						) : null}

						{!showCustomerRemarksInput ? (
							<Box>
								<Button
									size="sm"
									onClick={() => setShowCustomerRemarksInput(true)}
								>
									<Icons.Plus /> Add Customer Remarks
								</Button>
							</Box>
						) : null}

						{!showSupplierRemarksInput ? (
							<Box>
								<Button
									size="sm"
									onClick={() => setShowSupplierRemarksInput(true)}
								>
									<Icons.Plus /> Add Supplier Remarks
								</Button>
							</Box>
						) : null}
					</Inline>
				) : !showRemarksInput ? (
					<Box>
						<Button size="sm" onClick={() => setShowRemarksInput(true)}>
							<Icons.Plus /> Add Remarks
						</Button>
					</Box>
				) : (
					<Box maxWidth={"lg"}>
						<MarkdownInputField
							name={`remarks`}
							autoFocus={remarksValue ? false : true}
							label="Remarks for Supplier and Customer"
							secondaryLabel="optional"
							placeholder="Any notes/remarks that should be shared with Supplier and Customer..."
						/>
					</Box>
				)}
				<CheckboxInputField
					name="are_remarks_separated"
					label="Separate Remarks for Supplier & Customer"
				/>
			</Stack>

			{!showCommentsInput ? (
				<Box>
					<Button
						size="sm"
						onClick={() => setShowCommentsInput(true)}
						level="tertiary"
					>
						<Icons.Plus /> Add Comments
					</Button>
				</Box>
			) : (
				<Box maxWidth={"lg"}>
					<TextAreaInputField
						name={`comments`}
						autoFocus={commentsValue ? false : true}
						label="Comments for Internal Use Only"
						secondaryLabel="optional"
						placeholder="e.g. Need to talk with the supplier as AC not available."
					/>
				</Box>
			)}
			{!showTouristsInput ? (
				<Box>
					<Button
						size="sm"
						onClick={() => setShowTouristsInput(true)}
						level="tertiary"
					>
						<Icons.Plus /> Assign Tourists
					</Button>
				</Box>
			) : trip ? (
				<Card maxWidth={"3xl"}>
					<CardBody>
						<GetFieldValue<number | null> name="primary_contact">
							{({ value: primary_contact }) => (
								<GetFieldValue<
									TBookingTouristUpdate[] | undefined
								> name="tourists">
									{({ value: bookingTourists }) => (
										<TouristAssignmentInputField
											key={`tourists`}
											name={"members"}
											tripId={trip.id}
											allTourists={allTourists}
											onChange={(value) => {
												form.change("members", value as never)
												const tourists = (value || [])
													.filter((m) => m.member_type === "tourists")
													.map((m) => m.member as TTourist)
												const groups = (value || [])
													.filter((m) => m.member_type === "tourist_groups")
													.map((m) => m.member as TTouristGroup)
												const allUniqueTourists = [
													...tourists,
													...(groups || []).flatMap((g) =>
														(g || []).members.map((m) => m.tourist)
													),
												].filter(
													(t, index, self) =>
														self.findIndex((t2) => t2.id === t.id) === index
												)

												form.change("all_tourists", allUniqueTourists)
											}}
											primaryContact={primary_contact}
											onPrimaryContactChange={(touristId) => {
												form.change("primary_contact", touristId || null)
											}}
											bookingTourist={bookingTourists}
											onBookingTouristChange={(updatedTourists) => {
												form.change("tourists", updatedTourists)
											}}
										/>
									)}
								</GetFieldValue>
							)}
						</GetFieldValue>
					</CardBody>
				</Card>
			) : null}
		</Inline>
	)
}

export function CabWiseRemarkAndTouristAssignment({
	name,
	trip,
	showTouristsInput,
	setShowTouristsInput,
}: {
	name: string
	trip: NewScheduleRequestData["trip"]
	showTouristsInput: boolean
	setShowTouristsInput: (value: boolean) => void
}) {
	const form = useForm<ScheduleFormData>()
	const { value: customerRemarksValue } = useFieldValue<string | undefined>(
		`${name}.customer_remarks`
	)
	const { value: supplierRemarksValue } = useFieldValue<string | undefined>(
		`${name}.supplier_remarks`
	)
	const { value: remarksValue } = useFieldValue<string | undefined>(
		`${name}.remarks`
	)
	const { value: commentsValue } = useFieldValue<string | undefined>(
		`${name}.comments`
	)
	const { value: cabType } = useFieldValue<ICabType | undefined>(
		`${name}.cab_type`
	)
	const { value: allTourists } = useFieldValue<TTourist[] | undefined>(
		`${name}.all_tourists`
	)
	const { value: areRemarksSeparated } = useFieldValue<boolean>(
		`${name}.are_remarks_separated`
	)
	const [showRemarksInput, setShowRemarksInput] =
		useState<boolean>(!!remarksValue)

	const [showCustomerRemarksInput, setShowCustomerRemarksInput] =
		useState<boolean>(!!customerRemarksValue)

	const [showSupplierRemarksInput, setShowSupplierRemarksInput] =
		useState<boolean>(!!supplierRemarksValue)

	const [showCommentsInput, setShowCommentsInput] =
		useState<boolean>(!!commentsValue)

	const [showCabTouristsInput, setShowCabTouristsInput] = useState<boolean>(
		(allTourists || []).length ? true : false
	)

	return trip ? (
		<Col xs={12}>
			<Inline
				gap="4"
				collapseBelow={
					showRemarksInput ||
					showCommentsInput ||
					showCustomerRemarksInput ||
					showSupplierRemarksInput ||
					showCabTouristsInput
						? "xl"
						: undefined
				}
				flexWrap={"wrap"}
			>
				{/* If the cab type name is enclosed in square brackets, don't show the remarks input field. */}
				{cabType?.display_name ? (
					<Stack gap={"2"}>
						{areRemarksSeparated ? (
							<Inline
								gap={"4"}
								collapseBelow={
									showCustomerRemarksInput || showSupplierRemarksInput
										? "xl"
										: undefined
								}
								flexWrap={"wrap"}
							>
								{showCustomerRemarksInput ? (
									<Box flex="1" maxWidth="lg">
										<MarkdownInputField
											name={`${name}.customer_remarks`}
											autoFocus={!customerRemarksValue}
											label="Remarks for Customer"
											secondaryLabel="optional"
											placeholder="Any notes/remarks that should be shared with Customer..."
										/>
									</Box>
								) : null}
								{showSupplierRemarksInput ? (
									<Box flex="1" maxWidth="lg">
										<MarkdownInputField
											name={`${name}.supplier_remarks`}
											autoFocus={!supplierRemarksValue}
											label="Remarks for Supplier"
											secondaryLabel="optional"
											placeholder="Any notes/remarks that should be shared with Supplier..."
										/>
									</Box>
								) : null}
								{!showCustomerRemarksInput ? (
									<Box>
										<Button
											size="sm"
											onClick={() => setShowCustomerRemarksInput(true)}
										>
											<Icons.Plus /> Add Customer Remarks
										</Button>
									</Box>
								) : null}

								{!showSupplierRemarksInput ? (
									<Box>
										<Button
											size="sm"
											onClick={() => setShowSupplierRemarksInput(true)}
										>
											<Icons.Plus /> Add Supplier Remarks
										</Button>
									</Box>
								) : null}
							</Inline>
						) : !showRemarksInput ? (
							<Box>
								<Button size="sm" onClick={() => setShowRemarksInput(true)}>
									<Icons.Plus /> Add Remarks
								</Button>
							</Box>
						) : (
							<Box flex="1" maxWidth="lg">
								<MarkdownInputField
									name={`${name}.remarks`}
									autoFocus={!remarksValue}
									label="Remarks for Supplier and Customer"
									secondaryLabel="optional"
									placeholder="Any notes/remarks that should be shared with Supplier and Customer..."
								/>
							</Box>
						)}
						<CheckboxInputField
							name={`${name}.are_remarks_separated`}
							label={"Separate Remarks for Supplier & Customer"}
						/>{" "}
					</Stack>
				) : null}
				{!showCommentsInput ? (
					<Box>
						<Button
							size="sm"
							onClick={() => setShowCommentsInput(true)}
							level="tertiary"
						>
							<Icons.Plus /> Add Comments
						</Button>
					</Box>
				) : (
					<Box flex="1" maxWidth="lg">
						<TextAreaInputField
							name={`${name}.comments`}
							autoFocus={!commentsValue}
							label="Comments for Internal Use Only"
							secondaryLabel="optional"
							placeholder="e.g. Need to talk with the supplier as AC not available."
						/>
					</Box>
				)}
				<Stack gap="2" marginTop={!showCabTouristsInput ? "0" : "4"}>
					{!showCabTouristsInput ? (
						<Box>
							<Button
								size="sm"
								onClick={() => setShowCabTouristsInput(true)}
								level="tertiary"
							>
								<Icons.Plus /> Assign Tourists to Cab
							</Button>
						</Box>
					) : (
						<Card maxWidth={"3xl"}>
							<CardBody>
								<GetFieldValue<number | null> name={`${name}.primary_contact`}>
									{({ value: primary_contact }) => (
										<GetFieldValue<TBookingTouristUpdate[] | undefined>
											name={`${name}.tourists`}
										>
											{({ value: bookingTourists }) => {
												return (
													<TouristAssignmentInputField
														key={`${name}.tourists`}
														name={`${name}.members`}
														tripId={trip.id}
														allTourists={allTourists}
														onChange={(value) => {
															const cabMember = value
															const mainMembers =
																form.getState().values["members"] || []
															// Merge members while ensuring uniqueness by member.id
															const mergeMembers = [
																...(cabMember || []),
																...mainMembers,
															].filter(
																(member, index, self) =>
																	member &&
																	member.id !== undefined &&
																	self.findIndex(
																		(m) => m && m.id === member.id
																	) === index
															)
															form.change(
																`${name}.members` as never,
																value as never
															)
															form.change("members", mergeMembers as never)
															const tourists = (value || [])
																.filter((m) => m.member_type === "tourists")
																.map((m) => m.member as TTourist)
															const groups = (value || [])
																.filter(
																	(m) => m.member_type === "tourist_groups"
																)
																.map((m) => m.member as TTouristGroup)

															const allUniqueTourists = [
																...tourists,
																...(groups || []).flatMap((g) =>
																	(g || []).members.map((m) => m.tourist)
																),
															].filter(
																(t, index, self) =>
																	self.findIndex((t2) => t2.id === t.id) ===
																	index
															)

															const allMainTourists =
																(form.getState().values[`all_tourists`] as
																	| TTourist[]
																	| undefined) || []
															const mergeOfAllTourists = [
																...(allMainTourists || []),
																...allUniqueTourists,
															].filter(
																(t, index, self) =>
																	self.findIndex((t2) => t2.id === t.id) ===
																	index
															)

															form.change(
																`all_tourists` as never,
																mergeOfAllTourists as never
															)
															form.change(
																`${name}.all_tourists` as never,
																allUniqueTourists as never
															)
															if (!showTouristsInput) {
																setShowTouristsInput(true)
															}
														}}
														primaryContact={primary_contact}
														onPrimaryContactChange={(touristId) => {
															form.change(
																`${name}.primary_contact` as never,
																(touristId || null) as never
															)
														}}
														bookingTourist={bookingTourists}
														onBookingTouristChange={(updatedTourists) => {
															form.change(
																`${name}.tourists` as never,
																updatedTourists as never
															)
														}}
													/>
												)
											}}
										</GetFieldValue>
									)}
								</GetFieldValue>
							</CardBody>
						</Card>
					)}
				</Stack>
			</Inline>
		</Col>
	) : null
}
